import React from 'react';

interface TeamsIconProps {
  style?: object;
  /**
   * When true, render the full multi-color Microsoft Teams brand icon
   * (dark/purple shades + white "T"). When false/undefined, render the
   * monochrome silhouette using `currentColor` (suitable for buttons that
   * inherit the surrounding text color).
   */
  colored?: boolean;
  /** Pixel size for width/height. Defaults to 20. */
  size?: number;
}

export const TeamsIcon: React.FC<TeamsIconProps> = ({ style, colored, size = 20 }) => {
  if (colored) {
    return (
      <svg
        role="presentation"
        focusable="false"
        aria-hidden="true"
        viewBox="0 0 28 28"
        width={size}
        height={size}
        style={style}
      >
        <path
          d="M10 12.1201V19.5001C10 20.4011 10.221 21.2491 10.605 22.0001H15.604C16.312 22.0001 17 21.2871 17 20.6461V11.0211L16.719 11.0001H11.12C10.501 11.0001 10 11.5011 10 12.1201Z"
          fill="black"
        />
        <path
          d="M15.8334 8H12.8574C13.2834 9.338 14.5214 10.313 16.0004 10.313C16.3514 10.313 16.6824 10.244 17.0004 10.143V9.167C17.0004 8.525 16.4754 8 15.8334 8Z"
          fill="black"
        />
        <path
          d="M24.8728 11H19.8798L18.4858 12.127V17.716C18.4858 19.791 20.1678 21.473 22.2428 21.473C24.3178 21.473 25.9998 19.791 25.9998 17.716V12.127C25.9998 11.505 25.4948 11 24.8728 11Z"
          fill="#5059C9"
        />
        <path
          d="M25 7.5C25 8.881 23.881 10 22.5 10C21.119 10 20 8.881 20 7.5C20 6.119 21.119 5 22.5 5C23.881 5 25 6.119 25 7.5Z"
          fill="#5059C9"
        />
        <path
          d="M11.12 11H19.88C20.499 11 21 11.501 21 12.12V19.5C21 22.538 18.538 25 15.5 25C12.462 25 10 22.538 10 19.5V12.12C10 11.501 10.501 11 11.12 11Z"
          fill="#7B83EB"
        />
        <path
          d="M19.3133 7C19.3133 8.83 17.8303 10.313 16.0003 10.313C14.1703 10.313 12.6863 8.83 12.6863 7C12.6863 5.17 14.1703 3.687 16.0003 3.687C17.8303 3.687 19.3133 5.17 19.3133 7Z"
          fill="#7B83EB"
        />
        <path
          opacity="0.5"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.8573 8H15.8333C16.4753 8 17.0003 8.525 17.0003 9.167V10.143C16.6823 10.244 16.3513 10.313 16.0003 10.313C14.5213 10.313 13.2833 9.338 12.8573 8ZM10 19.5001V12.1201C10 11.5011 10.501 11.0001 11.12 11.0001H16.719L17 11.0211V20.6461C17 21.2871 16.312 22.0001 15.604 22.0001H10.605C10.221 21.2491 10 20.4011 10 19.5001Z"
          fill="black"
        />
        <path
          d="M14.8335 21H3.16649C2.52549 21 2.00049 20.475 2.00049 19.833V8.167C2.00049 7.525 2.52549 7 3.16649 7H14.8335C15.4755 7 16.0005 7.525 16.0005 8.167V19.833C16.0005 20.475 15.4755 21 14.8335 21Z"
          fill="#4B53BC"
        />
        <path
          d="M11.1806 11.5776H8.79158V17.9996H7.22158V11.5776H4.81958V9.9996H11.1806V11.5776Z"
          fill="white"
        />
      </svg>
    );
  }

  return (
    <svg
      role="presentation"
      focusable="false"
      aria-hidden="true"
      viewBox="0 0 28 28"
      width={size}
      height={size}
      fill="currentColor"
      style={style}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.0002 11H24.8732C25.4952 11 26.0002 11.505 26.0002 12.127V17.716C26.0002 19.791 24.3182 21.473 22.2432 21.473C21.7092 21.473 21.2052 21.356 20.7472 21.153C20.0452 23.383 17.9622 25 15.5002 25C12.9822 25 10.8602 23.308 10.2072 21H3.16724C2.52524 21 2.00024 20.475 2.00024 19.833V8.167C2.00024 7.525 2.52524 7 3.16724 7H12.6862C12.6862 5.17 14.1702 3.687 16.0002 3.687C17.8302 3.687 19.3132 5.17 19.3132 7C19.3132 8.83 17.8302 10.313 16.0002 10.313V11ZM16.0002 4.687C17.2752 4.687 18.3132 5.724 18.3132 7C18.3132 8.276 17.2752 9.313 16.0002 9.313V8.167C16.0002 7.525 15.4752 7 14.8332 7H13.6862C13.6862 5.724 14.7242 4.687 16.0002 4.687ZM7.97224 18.25V11.828H5.56924V9.75H12.4302V11.828H10.0422V18.25H7.97224ZM25.0002 17.716C25.0002 19.236 23.7632 20.473 22.2432 20.473C21.7782 20.473 21.3442 20.353 20.9622 20.146C20.9872 19.934 21.0002 19.719 21.0002 19.5V12.12C21.0002 12.08 20.9982 12.04 20.9932 12H24.8732C24.9432 12 25.0002 12.057 25.0002 12.127V17.716ZM15.5002 24C17.9812 24 20.0002 21.981 20.0002 19.5V12.12C20.0002 12.054 19.9462 12 19.8802 12H16.0002V19.833C16.0002 20.475 15.4752 21 14.8332 21H11.2572C11.8762 22.746 13.5442 24 15.5002 24ZM22.5002 6C23.3272 6 24.0002 6.673 24.0002 7.5C24.0002 8.327 23.3272 9 22.5002 9C21.6732 9 21.0002 8.327 21.0002 7.5C21.0002 6.673 21.6732 6 22.5002 6ZM25.0002 7.5C25.0002 8.881 23.8812 10 22.5002 10C21.1192 10 20.0002 8.881 20.0002 7.5C20.0002 6.119 21.1192 5 22.5002 5C23.8812 5 25.0002 6.119 25.0002 7.5Z"
      />
    </svg>
  );
};
